import { yupResolver } from "@hookform/resolvers/yup";
import { useHistory } from "react-router-dom";
import { Button } from "twComponents/Button";
import { FormDatePicker } from "components/FormHookComponents/FormDatePicker";
import { FormPriceInput } from "components/FormHookComponents/FormPriceInput";
import { FormInput } from "components/FormHookComponents/FormInput";
import { FormRadioGroup } from "components/FormHookComponents/FormRadioGroup";
import { FormRadio } from "components/FormHookComponents/FormRadio";
import { FormAddressBookSelect } from "components/FormHookComponents/FormAddressBookSelect";
import { TransactionValidationSchema as S } from "./validation";
import { transactionValidationSchema as validationSchema } from "./validation";
import { Modal, Text } from "components";
import {
  AnimalFragment,
  AnimalStatus,
  CreateFinancialTransactionInput,
  TransactionRecordEnumType,
  useCreateFinancialTransactionMutation,
  useFinancialTransactionAnimalsLazyQuery,
  useRemoveAnimalsFromFinancialTransactionMutation,
  ValueAllocationMethodEnumType,
  FinancialTransactionState,
  useAddAnimalsToFinancialTransactionMutation,
  useDeleteFinancialTransactionMutation,
  useUpdateFinancialTransactionMutation,
  UpdateFinancialTransactionInput,
  useFinancialTransactionLazyQuery,
} from "generated/graphql";
import { DateTime } from "luxon";
import { LoadingOverlay } from "twComponents/LoadingOverlay";
import { FormTextArea } from "components/FormHookComponents/FormTextArea";
import { useGetCurrentBusinessUnit } from "hooks/useGetCurrentBusinessUnit";
import { useContext, useEffect, useRef, useState } from "react";
import { FINANCIAL_TRANSACTIONS } from "constants/Routes";
import { AnimalsList } from "animals/AnimalsList";
import { TableOptionsContext } from "config/tableOptionsProvider";
import { Table } from "twComponents/Table";
import { removeNothings } from "helpers/general";
import { useRowSelection } from "hooks/useRowSelection";
import { FormCheckbox } from "components/FormHookComponents/FormCheckbox";
import { TABLE_IDS } from "constants/Interface";
import { useAnimalsColumns } from "hooks/useAnimalsColumns";
import { EmptyState } from "components/EmptyStates/Default";
import { CountryISOCode } from "helpers/translations/src/useHoldingInfo";
import { FormProvider, useForm } from "react-hook-form";
import { GET_FINANCIAL_TRANSACTION_ANIMALS } from "financial/api/queries";
import { useLazyPaginatedQuery } from "hooks/useLazyPaginatedQuery";
import { useHandleMutationResult } from "hooks/useHandleMutationResult";

export const TransactionForm = ({ transactionId }: { transactionId: string | undefined }) => {
  const [id, setId] = useState(transactionId);
  const isNewTransaction = !transactionId;
  const [isEditMode, setIsEditMode] = useState(!!isNewTransaction);
  const { handleError, handleMutationResult } = useHandleMutationResult();
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState<boolean>(false);
  const [shouldDeleteTransactionOnRouteChange, setShouldDeleteTransactionOnRouteChange] = useState(isNewTransaction);

  const history = useHistory();
  const { id: businessUnitId, weightUnits, countryIsoCodeUpperCase } = useGetCurrentBusinessUnit();
  const [createFinancialTransaction, { loading: isCreatingFinancialTransaction }] =
    useCreateFinancialTransactionMutation();
  const [updateFinancialTransaction, { loading: isUpdatingFinancialTransaction }] =
    useUpdateFinancialTransactionMutation();
  const [deleteFinancialTransaction, { loading: isDeletingFinancialTransaction }] =
    useDeleteFinancialTransactionMutation();
  const [animalsTableType, setAnimalsTableType] = useState<"transactionAnimalsTable" | "allAnimalsTable">(
    "transactionAnimalsTable",
  );
  const { selectedAnimalIds: animalIdsToRemove, resetSelection } = useRowSelection({
    tableId: "financial_transaction_animals_table",
  });
  const [blockedPath, setBlockedPath] = useState<string>();

  const [showNavigationModal, setShowNavigationModal] = useState<boolean>(false);
  const unblockHandle = useRef<() => void>();

  const handleConfirmNavigation = async () => {
    try {
      if (unblockHandle && unblockHandle.current) {
        unblockHandle.current();
      }
      if (id) {
        await deleteFinancialTransaction({
          variables: {
            input: {
              id,
              businessUnit: +businessUnitId,
            },
          },
        });
      }
    } catch (error: unknown) {
      handleError(error);
    } finally {
      if (blockedPath) {
        history.push(blockedPath);
      }
    }
  };

  useEffect(() => {
    unblockHandle.current = history.block(({ pathname }) => {
      if (shouldDeleteTransactionOnRouteChange) {
        setBlockedPath(pathname);
        setShowNavigationModal(true);
        return false;
      }
    });
    return () => {
      unblockHandle.current && unblockHandle.current();
    };
  }, [history, shouldDeleteTransactionOnRouteChange]);

  const methods = useForm<S>({
    mode: "onBlur",
    shouldFocusError: false,
    resolver: yupResolver(validationSchema),
    defaultValues: async () => {
      try {
        if (id) {
          const { data } = await fetchFinancialTransaction({ variables: { id, businessUnit: +businessUnitId } });

          return {
            name: data?.financialTransaction?.label || "",
            isSubmitted: data?.financialTransaction?.state === FinancialTransactionState.Submitted,
            type: data?.financialTransaction?.recordType || TransactionRecordEnumType.Sale,
            totalAmount: data?.financialTransaction?.amount?.toString() || "",
            perHeadAmount: data?.financialTransaction?.unitPrice?.toString() || "",
            date: data?.financialTransaction?.date
              ? DateTime.fromISO(data.financialTransaction.date).toJSDate()
              : DateTime.local().toJSDate(),
            description: data?.financialTransaction?.notes,
            grossWeight: data?.financialTransaction?.grossWeight || undefined,
            shrinkage: data?.financialTransaction?.shrinkage
              ? parseFloat((data.financialTransaction.shrinkage * 100).toFixed(2))
              : undefined,
            payWeight: data?.financialTransaction?.payWeight || undefined,
            contactId: data?.financialTransaction?.contact?.id || "",
            animalsCount: data?.financialTransaction?.animalsCount || 0,
          };
        } else {
          const input: CreateFinancialTransactionInput = {
            label: DateTime.local().toFormat(
              countryIsoCodeUpperCase === CountryISOCode.US ? "MM/dd/yyyy HH:mm:ss" : "dd/MM/yyyy HH:mm:ss",
            ),
            state: FinancialTransactionState.Draft,
            date: DateTime.local().toISO(),
            amount: 0,
            valueAllocationMethod: ValueAllocationMethodEnumType.PerHead,
            businessUnit: parseInt(businessUnitId),
            recordType: TransactionRecordEnumType.Sale,
          };

          const result = await createFinancialTransaction({
            variables: {
              input,
            },
          });

          const transaction = result.data?.createFinancialTransaction?.financialTransaction;

          setId(transaction?.id);
          setQueryVariables({ businessUnit: +businessUnitId, id: transaction?.id });

          return {
            name: transaction?.label || "",
            date: transaction?.date ? DateTime.fromISO(transaction.date).toJSDate() : DateTime.local().toJSDate(),
            isSubmitted: transaction?.state === FinancialTransactionState.Submitted,
            type: transaction?.recordType || TransactionRecordEnumType.Sale,
            totalAmount: "",
            perHeadAmount: "",
            description: "",
            grossWeight: undefined,
            shrinkage: undefined,
            payWeight: undefined,
            contactId: "",
            animalsCount: 0,
          };
        }
      } catch (e: unknown) {
        handleError(e);

        return {
          name: "",
          isSubmitted: false,
          type: TransactionRecordEnumType.Sale,
          totalAmount: "",
          date: DateTime.local().toJSDate(),
          description: "",
          perHeadAmount: "",
          grossWeight: undefined,
          shrinkage: undefined,
          payWeight: undefined,
          contactId: "",
          animalsCount: 0,
        };
      }
    },
  });

  const [removeAnimalsFromTransaction, { loading: isLoadingRemoveAnimals }] =
    useRemoveAnimalsFromFinancialTransactionMutation();
  const [addAnimalsToTransaction] = useAddAnimalsToFinancialTransactionMutation();

  const { columns, visibilityRules } = useAnimalsColumns({ isArchive: false });

  const { getTableOptions, setTableOptions } = useContext(TableOptionsContext);
  const tableOptions = getTableOptions<AnimalFragment>(TABLE_IDS.FINANCIAL_TRANSACTIONS_ALL_ANIMALS);

  const [fetchFinancialTransaction, { loading: isLoadingFinancialTransaction }] = useFinancialTransactionLazyQuery({
    variables: { businessUnit: +businessUnitId, id },
  });

  const [getTransactionAnimals, { loading: isLoadingTransactionAnimals, data: transactionAnimalsData }] =
    useFinancialTransactionAnimalsLazyQuery();

  const totalNumberOfItems = transactionAnimalsData?.financialTransaction?.animalsCount || 0;

  const { paginationProps, queryVariables, setQueryVariables } = useLazyPaginatedQuery({
    lazyQuery: getTransactionAnimals,
    initialQueryVariables: id ? { businessUnit: +businessUnitId, id } : undefined,
    tableId: "financial_transaction_animals_table",
    totalNumberOfItems,
    pageInfo: transactionAnimalsData?.financialTransaction?.transactionAnimals?.pageInfo,
    loading: isLoadingTransactionAnimals,
  });

  const onRowClick = (animal: AnimalFragment) => {
    history.push(`/system/livestock/animal/${animal.id}`);
  };

  const onSubmit = async (values: S) => {
    setShouldDeleteTransactionOnRouteChange(false);

    try {
      const input: UpdateFinancialTransactionInput = {
        id,
        state: values.isSubmitted ? FinancialTransactionState.Submitted : FinancialTransactionState.Draft,
        date: values.date.toISOString(),
        amount: parseFloat(values.totalAmount.replace(/,/g, "")),
        valueAllocationMethod: ValueAllocationMethodEnumType.PerHead,
        shrinkage: values.shrinkage ? parseFloat((values.shrinkage / 100).toFixed(4)) : null,
        label: values.name,
        businessUnit: parseInt(businessUnitId),
        contact: values.contactId ? parseInt(values.contactId) : null,
        recordType: values.type,
        notes: values.description || "",
        payWeight: values.payWeight || null,
        grossWeight: values.grossWeight || null,
        unitPrice: values.perHeadAmount ? parseFloat(values.perHeadAmount.replace(/,/g, "")) : null,
        upload: undefined,
      };

      const result = await updateFinancialTransaction({
        variables: {
          input,
        },
      });

      const errors = result.data?.updateFinancialTransaction?.errors || [];

      handleMutationResult({
        successMessage: "Financial transaction successfully saved.",
        errors,
        onSuccess: () => {
          history.push(`${FINANCIAL_TRANSACTIONS}`);
        },
      });
    } catch (error: unknown) {
      handleError(error);
    }
  };

  const onRemoveAnimals = async () => {
    try {
      const result = await removeAnimalsFromTransaction({
        variables: {
          input: {
            id,
            animals: animalIdsToRemove,
            businessUnit: +businessUnitId,
          },
        },
        refetchQueries: [{ query: GET_FINANCIAL_TRANSACTION_ANIMALS, variables: queryVariables }],
      });

      handleMutationResult({
        successMessage: "Successfully removed animals from transaction.",
        errors: result?.data?.removeAnimalsFromFinancialTransaction?.errors || [],
        onSuccess: () => {
          const countAnimalsOnTransaction =
            result.data?.removeAnimalsFromFinancialTransaction?.financialTransaction?.animalsCount || 0;

          methods.setValue("animalsCount", countAnimalsOnTransaction);
          methods.trigger("animalsCount");

          resetSelection();
        },
      });
    } catch (error: unknown) {
      handleError(error);
    }
  };

  const onDeleteFinancialTransaction = async () => {
    setShouldDeleteTransactionOnRouteChange(false);

    try {
      const result = await deleteFinancialTransaction({
        variables: {
          input: {
            id,
            businessUnit: +businessUnitId,
          },
        },
      });

      handleMutationResult({
        successMessage: "Successfully removed animals from transaction.",
        errors: result?.data?.deleteFinancialTransaction?.errors || [],
        onSuccess: () => {
          history.push(`${FINANCIAL_TRANSACTIONS}`);
        },
      });
    } catch (error: unknown) {
      handleError(error);
    }
  };

  const onAddAnimals = async () => {
    try {
      setAnimalsTableType("transactionAnimalsTable");
      const selectedRowIds = tableOptions?.selectedRowsIds;
      const animalIdsToAdd = selectedRowIds && selectedRowIds?.size > 0 ? Array.from(selectedRowIds) : [];

      const result = await addAnimalsToTransaction({
        variables: {
          input: {
            id,
            transactionAnimals: animalIdsToAdd.map((id) => {
              return { animal: id };
            }),
            businessUnit: +businessUnitId,
          },
        },
        refetchQueries: [{ query: GET_FINANCIAL_TRANSACTION_ANIMALS, variables: queryVariables }],
      });

      handleMutationResult({
        successMessage: "Successfully added animals to transaction.",
        errors: result?.data?.addAnimalsToFinancialTransaction?.errors || [],
        onSuccess: () => {
          const countAnimalsOnTransaction =
            result.data?.addAnimalsToFinancialTransaction?.financialTransaction?.animalsCount || 0;

          methods.setValue("animalsCount", countAnimalsOnTransaction);
          methods.trigger("animalsCount");

          setTableOptions({
            id: TABLE_IDS.FINANCIAL_TRANSACTIONS_ALL_ANIMALS,
            selectedRowsIds: new Set([]),
          });
        },
      });
    } catch (error: unknown) {
      handleError(error);
    }
  };

  const onCancel = () => {
    history.push(`${FINANCIAL_TRANSACTIONS}`);
  };

  const transactionAnimals = removeNothings(
    transactionAnimalsData?.financialTransaction?.transactionAnimals?.edges.map((edge) => edge?.node?.animal) || [],
  );

  if (isLoadingFinancialTransaction || isCreatingFinancialTransaction) {
    return <LoadingOverlay />;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="max-w-[100%]">
        <div className="px-[10px]">
          <div className="flex justify-between items-center">
            <Text className="font-semibold text-xl">Transaction</Text>
            <div className="flex-1 flex justify-end">
              <div className="flex items-center mr-4">
                <FormCheckbox<S> name="isSubmitted" disabled={!isEditMode} className="bg-white" />
                <label className="text-xs ml-2" htmlFor="isSubmitted">
                  Mark transaction as complete
                </label>
              </div>
              <Button
                type="button"
                disabled={isDeletingFinancialTransaction}
                variant="ghost"
                onClick={() => setShowDeleteConfirmationModal(true)}
                className="text-red-500 mr-2"
              >
                Delete
              </Button>
              {isNewTransaction ? null : (
                <Button
                  type="button"
                  variant="outline"
                  className="mr-2"
                  onClick={() => {
                    if (isEditMode) {
                      methods.reset();
                    }
                    setIsEditMode((prevState) => !prevState);
                  }}
                >
                  {isEditMode ? "Cancel" : "Edit"}
                </Button>
              )}
              {isEditMode ? (
                <Button
                  type="submit"
                  disabled={isUpdatingFinancialTransaction}
                  loading={isUpdatingFinancialTransaction}
                  className="ml-2"
                >
                  Save
                </Button>
              ) : (
                <Button type="button" disabled={isUpdatingFinancialTransaction} variant="outline" onClick={onCancel}>
                  Back
                </Button>
              )}
            </div>
          </div>
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1fr] mt-3">
            <FormInput<S>
              name="name"
              label="Name *"
              type="text"
              disabled={!isEditMode}
              className="disabled:text-black"
              autoComplete="off"
            />
            <FormDatePicker<S> label="Date *" name="date" disabled={!isEditMode} />
            <FormPriceInput<S> label="Total Price *" name="totalAmount" disabled={!isEditMode} placeholder="0" />
            <FormRadioGroup<S> name="type" label="Transaction Type *">
              <FormRadio<S>
                name="type"
                label="Sale"
                value={TransactionRecordEnumType.Sale}
                id="sale"
                disabled={!isEditMode}
              />
              <FormRadio<S>
                name="type"
                label="Purchase"
                value={TransactionRecordEnumType.Purchase}
                id="purchase"
                disabled={!isEditMode}
                className="ml-3"
              />
            </FormRadioGroup>
            <FormTextArea<S>
              label="Description"
              name="description"
              resize={false}
              disabled={!isEditMode}
              maxLength={50}
              rows={1}
              placeholder="Enter"
            />
            <FormPriceInput<S> label="Price per head" placeholder="0" name="perHeadAmount" disabled={!isEditMode} />
            <FormInput<S>
              name="grossWeight"
              label="Gross Weight"
              type="number"
              disabled={!isEditMode}
              className="disabled:text-black"
              adornmentLeft={{ label: weightUnits, labelFor: "grossWeight" }}
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
              step={0.01}
              placeholder="0"
            />
            <FormInput<S>
              name="payWeight"
              label="Pay Weight"
              type="number"
              disabled={!isEditMode}
              className="disabled:text-black"
              adornmentLeft={{ label: weightUnits, labelFor: "payWeight" }}
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
              step={0.01}
              placeholder="0"
            />
            <FormInput<S>
              name="shrinkage"
              label="Shrinkage"
              type="number"
              placeholder="0"
              adornmentLeft={{ label: "%", labelFor: "shrinkage" }}
              disabled={!isEditMode}
              className="disabled:text-black"
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
              step={0.01}
            />
            <FormAddressBookSelect label="Party" disabled={!isEditMode} />
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center px-2">
            <div>
              {methods.formState.errors.animalsCount?.message ? (
                <p className="text-status-warning text-xs">{methods.formState?.errors?.animalsCount?.message}</p>
              ) : null}
            </div>
            <div>
              {animalsTableType === "allAnimalsTable" ? (
                <>
                  <Button
                    onClick={() => {
                      setAnimalsTableType("transactionAnimalsTable");
                      setTableOptions({
                        id: TABLE_IDS.FINANCIAL_TRANSACTIONS_ALL_ANIMALS,
                        selectedRowsIds: new Set([]),
                      });
                    }}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button onClick={onAddAnimals} className="ml-2">
                    Save & Add Animals
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={onRemoveAnimals}
                    disabled={animalIdsToRemove.length === 0 || !isEditMode}
                    variant="destructive"
                    loading={isLoadingRemoveAnimals}
                  >
                    Remove Animals
                  </Button>
                  <Button
                    onClick={() => {
                      setAnimalsTableType("allAnimalsTable");
                    }}
                    disabled={!isEditMode}
                    className="ml-2"
                  >
                    Add Animals
                  </Button>
                </>
              )}
            </div>
          </div>
          {animalsTableType === "allAnimalsTable" ? (
            <AnimalsList
              tableId={"financial_transaction_all_animals_table"}
              showSelectColumn
              tableFilterIdentifier="animalsList"
              showAnimalActionButtons={false}
              showIndividualAnimalActionButtons={false}
              animalStatus={AnimalStatus.Any}
              resetSelectedAnimalsOnUnmount={false}
              animalTypeIds={[2, 3]} // Get both sheep and cattle
            />
          ) : (
            <div className="px-2 mt-2">
              <Table
                tableId="financial_transaction_animals_table"
                options={{ data: transactionAnimals, columns, getRowId: (row) => row.id, enableSorting: false }}
                columnVisibility={visibilityRules}
                loading={isLoadingTransactionAnimals}
                showSelectColumn={isEditMode && methods.watch("animalsCount") > 0}
                onRowClick={isEditMode ? undefined : onRowClick}
                paginationProps={paginationProps}
                emptyStateComponent={
                  <EmptyState
                    title="You haven't added any animals yet."
                    description="Add animals by clicking 'Add animals' above."
                  />
                }
              />
            </div>
          )}
        </div>
      </form>

      <Modal
        active={showDeleteConfirmationModal}
        title="Delete Draft"
        subTitle="Are you sure you want to delete this draft?"
        actions={{
          primary: {
            caption: "Confirm",
            disabled: isDeletingFinancialTransaction,
            requesting: isDeletingFinancialTransaction,
            onClick: onDeleteFinancialTransaction,
          },
          secondary: {
            caption: "Cancel",
            onClick: () => setShowDeleteConfirmationModal(false),
          },
        }}
      />
      <Modal
        active={showNavigationModal}
        title="Are you sure?"
        subTitle="This transaction will be lost if you exit the page."
        actions={{
          primary: {
            caption: "Confirm",
            disabled: isDeletingFinancialTransaction,
            requesting: isDeletingFinancialTransaction,
            onClick: handleConfirmNavigation,
          },
          secondary: {
            caption: "Cancel",
            onClick: () => setShowNavigationModal(false),
          },
        }}
      />
    </FormProvider>
  );
};
